import React from 'react';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="p-6 max-w-4xl mx-auto bg-surface-card shadow-md rounded-xl mt-6">
      <h1 className="font-display tracking-tight text-3xl font-bold mb-6 text-text-primary">Privacy Policy</h1>
      <p className="text-sm text-text-muted mb-6">Last Updated: February 12, 2026</p>

      <div className="space-y-4 text-text-body">
        <p>
          At Sync EHS, LLC (“Sync EHS,” “We,” “Us,” or “Our”), we are committed to protecting your privacy. This Privacy Policy explains how we collect, use, store, and protect your data when you use the Sync EHS platform (the “Platform”), which includes our web application, APIs, dashboards, reports, and related services for safety management. By using the Platform, you agree to the practices described in this Privacy Policy.
        </p>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">1. Information We Collect</h2>
          <p>We collect the following types of information:</p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li><strong>Account Information:</strong> When you create an account, we collect your name, email address, company name, and payment information to set up and manage your subscription.</li>
            <li><strong>Safety Data:</strong> You may upload safety-related data, such as employee names, incident details, dates, and descriptions, to track and manage workplace safety. This may include protected health information (PHI) under HIPAA.</li>
            <li><strong>Usage Data:</strong> We collect anonymized usage metrics, such as the number of logins, feature usage, and page views, to improve the Platform. This data does not identify you or your employees.</li>
            <li><strong>Technical Data:</strong> We collect technical information, such as your IP address, browser type, device type, and operating system, to ensure the Platform functions properly and to diagnose issues.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">2. How We Use Your Information</h2>
          <p>We use your information to:</p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Provide and maintain the Platform’s functionality, such as generating safety reports and dashboards.</li>
            <li>Process payments and manage your subscription.</li>
            <li>Communicate with you, including responding to inquiries, sending service updates, and providing technical support.</li>
            <li>Improve the Platform by analyzing anonymized usage metrics (e.g., identifying popular features).</li>
            <li>Comply with legal obligations, such as responding to court orders or regulatory requests.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">3. How We Share Your Information</h2>
          <p>We do not sell or share your personal data with third parties for marketing purposes. We may share your data in the following circumstances:</p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li><strong>Service Providers:</strong> We share data with trusted service providers (e.g., cloud hosting services, payment processors) to deliver and improve the Platform. These providers are contractually obligated to protect your data.</li>
            <li><strong>Legal Requirements:</strong> We may disclose data if required by law, such as in response to a court order, subpoena, or regulatory request.</li>
            <li><strong>Business Transfers:</strong> If Sync EHS is involved in a merger, acquisition, or sale of assets, your data may be transferred as part of that transaction, but we will notify you and ensure continued protection of your data.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">4. Data Security</h2>
          <p>We take data security seriously and implement industry-standard measures to protect your information:</p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>All data is encrypted at rest and in transit using modern protocols (e.g., AES-256, TLS 1.2).</li>
            <li>We comply with HIPAA requirements for handling protected health information (PHI) and implement administrative, physical, and technical safeguards. We will enter into a Business Associate Agreement (BAA) upon request for covered entities.</li>
            <li>We maintain regular backups, but you are responsible for maintaining your own backups of critical data, as Sync EHS is not liable for data loss due to unforeseen events (e.g., server failures, cyberattacks).</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">5. Your Data Rights</h2>
          <p>You have control over your data and the following rights:</p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li><strong>Access and Export:</strong> You can request a copy of your account data in a standard format (e.g., CSV) by contacting support@syncehs.com.</li>
            <li><strong>Correction:</strong> You can update or correct your account information through the Platform or by contacting us.</li>
            <li><strong>Deletion:</strong> You can request deletion of your data at any time by contacting support@syncehs.com. After deletion, data is retained for 30 days for backup purposes and then permanently deleted, unless required by law.</li>
            <li><strong>Opt-Out:</strong> You can opt out of non-essential communications (e.g., marketing emails) by clicking the unsubscribe link in those emails or contacting us.</li>
          </ul>
          <p>We comply with applicable privacy laws, including GDPR (for EU users) and CCPA (for California residents), ensuring your rights to access, correct, or delete your data.</p>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">6. Data Retention</h2>
          <p>
            We retain your account data for as long as your account is active. After account termination, data is retained for 30 days, during which you may request an export, and then permanently deleted, unless required by law (e.g., for tax or audit purposes). Anonymized usage metrics may be retained indefinitely for analytics purposes.
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">7. International Data Transfers</h2>
          <p>
            Sync EHS is based in the United States, and your data is stored on servers located in the U.S. If you access the Platform from outside the U.S., your data will be transferred to the U.S., where privacy laws may differ. By using the Platform, you consent to this transfer and agree to the terms of this Privacy Policy.
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">8. Cookies and Tracking</h2>
          <p>
            We use cookies and similar technologies to enhance your experience on the Platform, such as maintaining your login session and analyzing usage patterns. You can manage cookie preferences through your browser settings, but disabling cookies may affect the Platform’s functionality.
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">9. Children’s Privacy</h2>
          <p>
            The Platform is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from children under 18. If you believe we have collected such information, please contact us at support@syncehs.com to have it removed.
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">10. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. We will notify you of material changes via email or in-app notification at least 30 days before they take effect. Continued use of the Platform after the effective date constitutes acceptance of the updated Privacy Policy.
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">11. Contact Us</h2>
          <p>If you have questions about this Privacy Policy or your data, please contact us at:</p>
          <p>Email: <a href="mailto:support@syncehs.com" className="text-accent-blue hover:underline">support@syncehs.com</a></p>
          <p>We aim to respond to all inquiries within 1–2 business days.</p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
